// コメントピン（吹き出し）。
// - 投稿後はコメント本文を隠し、投稿者アバターを載せたピンのみを残す。
// - スレッドに複数投稿者がいる場合はアバターを重ねて表示する（最大 3 名 + 余剰は「+N」）。
// - マウスホバー中のみ、起点コメント（スレッドに残っている最古のメッセージ）の投稿者名・本文・
//   投稿時刻のプレビューを表示する。本文は Markdown 記法のマーカーを除去した「画面に見える内容」
//   （リンクは表示テキストへ縮約）で表示する。
// - クリックでスレッドのポップオーバー（返信・編集・削除）を開く。
//
// ビューポート固定サイズで描画するため、x/y（アンカー）から射影した screen 座標に配置する。
// ピンの左下角をアンカー（クリック位置）に合わせる。

import {
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useShowConfirmDialog } from "@meiden-connect/ui";
import {
  canDeleteCommentThread,
  commentThreadParticipants,
  type CommentThreadCanvasObject,
} from "@refinement-canvas/domain";
import { stripMarkdownMarkersForDisplay } from "../../../lib/markdown-text";
import { CommentAvatar } from "./comment-avatar";
import { confirmDeleteCommentThread } from "./comment-thread-delete";
import { formatCommentTimestamp } from "./comment-time";

interface Props {
  readonly thread: CommentThreadCanvasObject;
  /** アンカー（左下角）の screen 座標。 */
  readonly screenX: number;
  readonly screenY: number;
  /** 現在のユーザー id（自分のアバターは allowlist 制限を外して表示するため）。 */
  readonly currentUserId: string;
  /** 管理者か。スレッド削除可否（管理者 or 全メッセージが自分の投稿）の判定に使う。 */
  readonly canAdmin: boolean;
  /** 参照専用（埋め込み）。true でクリック（開く）だけ許可し、移動 / 右クリック削除を無効化する。 */
  readonly readOnly?: boolean | undefined;
  /** 現在のズーム倍率。screen 上のドラッグ量を stage 座標へ換算するのに使う。 */
  readonly zoom: number;
  readonly isOpen: boolean;
  readonly onOpen: () => void;
  /** 右クリックでこのスレッドを削除する（確認ダイアログ後に呼ぶ。最終判定はサーバ）。 */
  readonly onDelete: () => void;
  /**
   * 移動ドラッグ中の screen オフセット（しきい値超えで {dx,dy}、確定/中断で null）を親へ通知する。
   * 親（CommentLayer）はこれで開いているポップオーバーをピンと一緒に追従させる。これにより
   * 「ピンだけ動いて本文が取り残される」も「ドラッグでポップオーバーを閉じて未送信テキストを失う」も避ける。
   */
  readonly onDragOffsetChange: (offset: { dx: number; dy: number } | null) => void;
  /** ドラッグ確定時に、ピンを移動した先の stage 座標（x / y）を通知する。 */
  readonly onMove: (x: number, y: number) => void;
}

const AVATAR_SIZE = 28;
const MAX_VISIBLE_AVATARS = 3;
// クリック（=スレッドを開く）と移動ドラッグを区別する閾値（screen px）。
const DRAG_THRESHOLD_PX = 4;

interface PinDragState {
  readonly pointerId: number;
  readonly startClientX: number;
  readonly startClientY: number;
  moved: boolean;
}

export function CommentPin({
  thread,
  screenX,
  screenY,
  currentUserId,
  canAdmin,
  readOnly = false,
  zoom,
  isOpen,
  onOpen,
  onDelete,
  onDragOffsetChange,
  onMove,
}: Props): JSX.Element {
  const showConfirmDialog = useShowConfirmDialog();
  const [hovered, setHovered] = useState(false);
  // ドラッグ中の screen オフセット（見た目の追従用）。ドラッグしていないときは null。
  const [dragOffset, setDragOffset] = useState<{ dx: number; dy: number } | null>(null);
  const dragRef = useRef<PinDragState | null>(null);
  // 直前の pointer 操作が移動ドラッグだったか。移動直後の click でスレッドを開かないために使う。
  const justDraggedRef = useRef(false);
  const participants = useMemo(() => commentThreadParticipants(thread), [thread]);
  const root = thread.messages[0];

  const visible = participants.slice(0, MAX_VISIBLE_AVATARS);
  const overflow = participants.length - visible.length;

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>): void => {
    // キャンバスのパン等へ伝播させない。
    // 配置ツール（place-comment 等）選択中でも、ピン上の pointerdown は stage 配置へ譲らず、
    // ここで止めてピンのドラッグ移動 / スレッド開閉を優先する。これは意図的な仕様で、本機能が範とする
    // FigJam と同じ挙動（コメントモード中に既存コメントをクリックすると、新規コメントを重ねて作るのではなく
    // その既存スレッドを開いて返信できる）。要望②「図形の上にコメント作成」は「図形（通常オブジェクト）」が
    // 対象で、既存コメントピンの真上への新規重ね置きは対象外のため、ピンは常に操作可能なまま残す。
    event.stopPropagation();
    if (event.button !== 0) return;
    // 参照専用ではピンの移動を行わない（クリックでの開閉だけ許可する。onClick は別途発火する）。
    if (readOnly) return;
    // 新しい操作の開始時に「直前がドラッグだった」フラグをクリアする。
    // ドラッグ後に click が発火しない環境でフラグが残り、次のクリックを誤って握りつぶすのを防ぐ。
    justDraggedRef.current = false;
    dragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      moved: false,
    };
    // jsdom などポインタキャプチャ未実装の環境でも落ちないよう存在チェックする。
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startClientX;
    const dy = event.clientY - drag.startClientY;
    if (!drag.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      // ドラッグ中は（ピンの）ホバーカードを抑止する。ポップオーバーは閉じず、親が同じオフセットで
      // 追従させるため、未送信の返信・編集中テキストは失われない。
      setHovered(false);
    }
    setDragOffset({ dx, dy });
    // 開いているポップオーバーをピンと一緒に動かせるよう、現在のドラッグ量を親へ通知する。
    onDragOffsetChange({ dx, dy });
  };

  // commit=true（pointerup＝利用者のドロップ）のときだけ移動を確定する。
  // commit=false（pointercancel）はブラウザがジェスチャを中断したケース（パームリジェクション・
  // 別ポインタ割り込み・ブラウザ独自ジェスチャへの横取り等）で、利用者の意図したドロップではない。
  // 中断時に確定すると意図しない位置へピンが移ってしまうため、確定せず破棄して元位置を維持する。
  const finishDrag = (event: ReactPointerEvent<HTMLDivElement>, commit: boolean): void => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    if (commit && drag.moved) {
      const dx = event.clientX - drag.startClientX;
      const dy = event.clientY - drag.startClientY;
      // screen 上のドラッグ量を stage 座標へ換算し、新しいアンカー位置を確定する。
      // （境界 clamp は op 適用側 clampCanvasObjectToCanvas が担う）。
      onMove(thread.x + dx / zoom, thread.y + dy / zoom);
      justDraggedRef.current = true;
    }
    // commit/cancel いずれでも見た目オフセットは破棄する。commit 時はストア更新で新位置へ収束し、
    // cancel 時は元位置（thread.x/y）へ戻る。ポップオーバーの追従も解除する。
    setDragOffset(null);
    onDragOffsetChange(null);
  };

  // スレッド削除の権限（クライアント UI とサーバで同じ domain 関数を正本にする）。
  const canDelete = canDeleteCommentThread(thread, currentUserId, canAdmin);

  // 右クリックでスレッドを削除する。確認ダイアログ後に親へ通知（onDelete）する。
  // 右クリックは handlePointerDown が button!==0 で早期 return するためドラッグ・開閉には影響しない。
  const handleContextMenu = async (event: ReactMouseEvent<HTMLDivElement>): Promise<void> => {
    // 参照専用では右クリック削除を行わない（ブラウザ標準メニューに委ねる）。
    if (readOnly) return;
    // 削除権限が無い場合は何もせず、ブラウザ標準のコンテキストメニューに委ねる。
    // ここで preventDefault してしまうと、標準メニューも削除確認も出ず「右クリックしても無反応」となり、
    // 操作意図が伝わらないため（権限判定より前に握り潰さない）。
    if (!canDelete) return;
    // 権限がある場合のみ、ピン上では標準メニューを出さず、キャンバスのパン等へも伝播させずに削除確認を出す。
    event.preventDefault();
    event.stopPropagation();
    if (await confirmDeleteCommentThread(showConfirmDialog)) onDelete();
  };

  const pinStyle: CSSProperties = {
    position: "absolute",
    left: `${screenX}px`,
    top: `${screenY}px`,
    // 左下角をアンカーに合わせる。ドラッグ中は screen オフセットを上乗せして見た目を追従させる。
    transform: dragOffset
      ? `translate(${dragOffset.dx}px, ${dragOffset.dy}px) translateY(-100%)`
      : "translateY(-100%)",
    transformOrigin: "left bottom",
    display: "inline-flex",
    alignItems: "center",
    gap: "0px",
    padding: "4px",
    background: "var(--color-surface, #ffffff)",
    // 左下だけ角を尖らせて「ピン」らしくする（吹き出しの尾）。
    borderRadius: "18px 18px 18px 4px",
    boxShadow: isOpen
      ? "0 0 0 2px var(--color-accent, #7c4dff), 0 4px 14px rgba(0,0,0,0.18)"
      : "0 2px 8px rgba(0,0,0,0.18)",
    cursor: readOnly ? "pointer" : dragOffset ? "grabbing" : "grab",
    pointerEvents: "auto",
    userSelect: "none",
    touchAction: "none",
  };

  return (
    <div
      data-object-type="comment-thread"
      data-comment-ui=""
      data-testid={`comment-pin-${thread.id}`}
      style={pinStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={(event) => finishDrag(event, true)}
      onPointerCancel={(event) => finishDrag(event, false)}
      onContextMenu={(event) => void handleContextMenu(event)}
      onClick={(event) => {
        event.stopPropagation();
        // 直前が移動ドラッグだった場合は、その後に発火する click でスレッドを開かない。
        if (justDraggedRef.current) {
          justDraggedRef.current = false;
          return;
        }
        onOpen();
      }}
      role="button"
      aria-label="コメントを開く"
    >
      <div style={{ display: "inline-flex", alignItems: "center" }}>
        {visible.map((participant, index) => (
          <div
            key={participant.authorId}
            style={{ marginLeft: index === 0 ? 0 : "-10px", zIndex: visible.length - index }}
          >
            <CommentAvatar
              name={participant.authorName}
              src={participant.authorAvatar}
              size={AVATAR_SIZE}
              title={participant.authorName}
              isSelf={participant.authorId === currentUserId}
            />
          </div>
        ))}
        {overflow > 0 ? (
          <span
            aria-hidden="true"
            style={{
              marginLeft: "-10px",
              width: `${AVATAR_SIZE}px`,
              height: `${AVATAR_SIZE}px`,
              borderRadius: "50%",
              border: "2px solid #ffffff",
              background: "var(--color-border, #d4d4dd)",
              color: "var(--color-text, #1a1a2e)",
              display: "grid",
              placeItems: "center",
              fontSize: "11px",
              fontWeight: 700,
              boxSizing: "border-box",
            }}
          >
            +{overflow}
          </span>
        ) : null}
      </div>

      {hovered && !isOpen && !dragOffset && root ? (
        <HoverCard
          authorName={root.authorName}
          body={root.body}
          createdAt={root.createdAt}
          replyCount={Math.max(0, thread.messages.length - 1)}
          imageCount={root.images?.length ?? 0}
        />
      ) : null}
    </div>
  );
}

// ホバー時のプレビューカード。起点コメント（スレッドに残っている最古のメッセージ）の投稿者・
// 本文・時刻と、添付画像の件数・返信件数を表示する。
// 本文は生の Markdown ではなく、記法マーカーを除去した「画面に見える内容」（リンクは表示テキストへ
// 縮約）を表示する。数行の line-clamp プレビューのため、見出しの拡大整形はせずプレーンに揃える
// （サムネイルの先頭プレビューと同じ stripMarkdownMarkersForDisplay の方針）。
function HoverCard({
  authorName,
  body,
  createdAt,
  replyCount,
  imageCount,
}: {
  readonly authorName: string;
  readonly body: string;
  readonly createdAt: string;
  readonly replyCount: number;
  /** 起点コメントの添付画像の枚数（FigJam のホバーカード同様、件数だけを示す）。 */
  readonly imageCount: number;
}): JSX.Element {
  return (
    <div
      role="tooltip"
      style={{
        position: "absolute",
        left: 0,
        bottom: "calc(100% + 6px)",
        width: "240px",
        background: "var(--color-surface, #ffffff)",
        borderRadius: "10px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.22)",
        padding: "10px 12px",
        cursor: "default",
        pointerEvents: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: "6px", marginBottom: "4px" }}>
        <span
          style={{
            fontWeight: 700,
            fontSize: "13px",
            color: "var(--color-text, #1a1a2e)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "150px",
          }}
        >
          {authorName || "利用者"}
        </span>
        <span style={{ fontSize: "11px", color: "var(--color-text-muted, #6b6f7d)" }}>
          {formatCommentTimestamp(createdAt)}
        </span>
      </div>
      <div
        style={{
          fontSize: "13px",
          color: "var(--color-text, #1a1a2e)",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          display: "-webkit-box",
          WebkitLineClamp: 4,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {stripMarkdownMarkersForDisplay(body)}
      </div>
      {imageCount > 0 ? (
        <div
          style={{
            marginTop: "6px",
            fontSize: "11px",
            color: "var(--color-text-muted, #6b6f7d)",
          }}
        >
          画像 {imageCount} 件
        </div>
      ) : null}
      {replyCount > 0 ? (
        <div
          style={{
            marginTop: "6px",
            fontSize: "11px",
            color: "var(--color-text-muted, #6b6f7d)",
          }}
        >
          返信 {replyCount} 件
        </div>
      ) : null}
    </div>
  );
}
